// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Pure analysis helpers — testable, no DOM.

import type { Facility, Substance, StateYearRow, Meta, YearTotal } from './types';

// Year-over-year change for a facility's total emissions.
export interface YearChange {
  prev: number;
  cur: number;
  delta: number;
  pct: number | null;
}

export function facilityYoY(facility: Facility, year: number): YearChange | null {
  const cur = facility.history[String(year)];
  const prev = facility.history[String(year - 1)];
  if (!cur || !prev) return null;
  const curTotal = cur.a + cur.w + cur.l;
  const prevTotal = prev.a + prev.w + prev.l;
  return {
    prev: prevTotal,
    cur: curTotal,
    delta: curTotal - prevTotal,
    pct: prevTotal === 0 ? null : ((curTotal - prevTotal) / prevTotal) * 100
  };
}

// Per-capita totals by state (kg per 1,000 residents).
export function perCapitaByState(meta: Meta): Array<{ state: string; perCapita: number; total: number; population: number }> {
  const out: Array<{ state: string; perCapita: number; total: number; population: number }> = [];
  for (const [state, totals] of Object.entries(meta.state_totals)) {
    const pop = meta.state_population[state];
    if (!pop) continue;
    out.push({
      state,
      perCapita: totals.total / (pop / 1000),
      total: totals.total,
      population: pop
    });
  }
  out.sort((a, b) => b.perCapita - a.perCapita);
  return out;
}

// Filter facilities by free-text search across name, suburb, postcode, ABN, business name.
export function searchFacilities(
  facilities: Facility[],
  query: string,
  limit = 50
): Facility[] {
  const q = query.trim().toLowerCase();
  if (!q) return facilities.slice(0, limit);
  const tokens = q.split(/\s+/).filter(Boolean);
  const results: Array<{ f: Facility; score: number }> = [];
  for (const f of facilities) {
    const haystack = [
      f.name,
      f.business_name,
      f.suburb,
      f.state,
      f.postcode,
      f.abn,
      f.anzsic_class_name,
      f.division_name
    ].filter(Boolean).join(' | ').toLowerCase();
    let score = 0;
    let matched = true;
    for (const t of tokens) {
      if (!haystack.includes(t)) { matched = false; break; }
      // Prefer matches in name and suburb.
      if (f.name.toLowerCase().includes(t)) score += 3;
      if (f.suburb?.toLowerCase().includes(t)) score += 2;
      if (f.postcode?.includes(t)) score += 4;
      score += 1;
    }
    if (matched) results.push({ f, score: score + Math.log10(f.total + 10) });
  }
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit).map(r => r.f);
}

// Bucket state-year rows by state.
export function groupStateYear(rows: StateYearRow[]): Map<string, StateYearRow[]> {
  const m = new Map<string, StateYearRow[]>();
  for (const r of rows) {
    let arr = m.get(r.state);
    if (!arr) { arr = []; m.set(r.state, arr); }
    arr.push(r);
  }
  for (const arr of m.values()) arr.sort((a, b) => a.year - b.year);
  return m;
}

// Top-N substances by lifetime total.
export function topSubstances(substances: Substance[], n = 10): Substance[] {
  return [...substances].sort((a, b) => b.total - a.total).slice(0, n);
}

// Identify substance trend direction (last 5 years).
export function substanceTrend(substance: Substance, latestYear: number): 'up' | 'down' | 'flat' | 'unknown' {
  const recent: number[] = [];
  for (let y = latestYear - 4; y <= latestYear; y++) {
    const v = substance.by_year[String(y)];
    if (v) recent.push(v.a + v.w + v.l);
  }
  if (recent.length < 3) return 'unknown';
  const first = recent[0];
  const last = recent[recent.length - 1];
  if (first === 0) return last > 0 ? 'up' : 'flat';
  const change = (last - first) / first;
  if (change > 0.1) return 'up';
  if (change < -0.1) return 'down';
  return 'flat';
}

// Total emission of the latest reporting year nationally.
export function latestYearTotal(yearTotals: YearTotal[]): number {
  if (yearTotals.length === 0) return 0;
  const last = yearTotals[yearTotals.length - 1];
  return last.air + last.water + last.land;
}

// 5-year window national totals — for trend cards.
export function rollingChange(yearTotals: YearTotal[], window = 5): number | null {
  if (yearTotals.length < window + 1) return null;
  const recent = yearTotals.slice(-window);
  const earlier = yearTotals.slice(-window - 1, -1);
  const sum = (rows: YearTotal[]) => rows.reduce((acc, r) => acc + r.air + r.water + r.land, 0);
  const r = sum(recent);
  const e = sum(earlier);
  if (e === 0) return null;
  return ((r - e) / e) * 100;
}
